import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader,
  Save,
  Unlink,
  Users,
  Archive,
  CalendarClock,
} from 'lucide-react';

import { eventTeamApiService } from '../../services/eventTeamApi';
import { kanbanTeamColor } from '../../utils/kanbanTeamColors';
import { cn } from '../../utils/cn';
import { ConfirmationModal } from '../ConfirmationModal';
import { KanbanListRoleBadge } from '../kanban/KanbanListRoleBadge';
import { KanbanPriorityBadge } from '../kanban/KanbanPriorityBadge';
import type {
  EventKanbanLink,
  EventWorkspaceSummary,
  UpdateEventKanbanLinkRequest,
} from '../../types/eventTeam';

type SyncFlagKey = keyof Pick<
  EventKanbanLink,
  'sync_status' | 'sync_priority' | 'sync_due_date' | 'sync_assignees' | 'sync_comments' | 'propagate_watchers'
>;

const SYNC_FLAGS: Array<{ key: SyncFlagKey; label: string; hint: string }> = [
  { key: 'sync_status', label: 'Status', hint: 'Sync status' },
  { key: 'sync_priority', label: 'Priority', hint: 'Sync priority' },
  { key: 'sync_due_date', label: 'Due date', hint: 'Sync due date' },
  { key: 'sync_assignees', label: 'Assignees', hint: 'Sync assignees' },
  { key: 'sync_comments', label: 'Comments', hint: 'Mirror comments' },
  { key: 'propagate_watchers', label: 'Watchers', hint: 'Propagate watchers' },
];

export interface EventBridgeRowProps {
  eventId: string;
  summary: EventWorkspaceSummary;
  canManage: boolean;
  onChanged: () => void;
}

/**
 * One row inside `<EventWorkspacesSection>` — board+card snapshot, all six sync flag
 * toggles (incl. `sync_due_date` — guide §1.5), notes editor, and unlink action.
 */
export const EventBridgeRow = ({ eventId, summary, canManage, onChanged }: EventBridgeRowProps) => {
  const initialLink = summary.link;
  const [link, setLink] = useState<EventKanbanLink>(initialLink);
  const [notes, setNotes] = useState<string>(initialLink.notes ?? '');
  const [expanded, setExpanded] = useState(false);
  const [savingFlags, setSavingFlags] = useState<Record<string, boolean>>({});
  const [savingNotes, setSavingNotes] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLink(summary.link);
    setNotes(summary.link.notes ?? '');
  }, [summary.link]);

  const teamColor = useMemo(() => kanbanTeamColor(summary.board.team), [summary.board.team]);

  const cardHref = `/workspaces/${encodeURIComponent(summary.board.id)}?card=${encodeURIComponent(summary.card.id)}`;
  const boardHref = `/workspaces/${encodeURIComponent(summary.board.id)}`;

  const notesDirty = notes !== (link.notes ?? '');

  const handleToggleFlag = async (key: SyncFlagKey) => {
    if (!canManage) return;
    const next = link[key] !== 1;
    const optimistic: EventKanbanLink = { ...link, [key]: next ? 1 : 0 };
    setLink(optimistic);
    setSavingFlags((s) => ({ ...s, [key]: true }));
    setError(null);
    try {
      const body: UpdateEventKanbanLinkRequest = { [key]: next };
      const updated = await eventTeamApiService.updateEventKanbanLink(eventId, summary.card.id, body);
      setLink(updated);
      onChanged();
    } catch (err) {
      setLink(link);
      setError(err instanceof Error ? err.message : `Failed to update ${key}`);
    } finally {
      setSavingFlags((s) => ({ ...s, [key]: false }));
    }
  };

  const handleSaveNotes = async () => {
    if (!canManage || !notesDirty) return;
    setSavingNotes(true);
    setError(null);
    try {
      const updated = await eventTeamApiService.updateEventKanbanLink(eventId, summary.card.id, {
        notes: notes.trim() === '' ? null : notes,
      });
      setLink(updated);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleUnlink = async () => {
    setDeleting(true);
    setError(null);
    try {
      await eventTeamApiService.deleteEventKanbanLink(eventId, summary.card.id);
      setConfirmDelete(false);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink');
      setDeleting(false);
    }
  };

  return (
    <div className="subcard overflow-hidden">
      <div className="flex items-start gap-3 p-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="btn-icon btn-icon-sm -ml-1"
          title={expanded ? 'Collapse' : 'Expand'}
          aria-label={expanded ? 'Collapse bridge details' : 'Expand bridge details'}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={cardHref}
              className="text-sm font-semibold text-white hover:text-accent-300 transition-colors truncate"
              title={summary.card.title}
            >
              {summary.card.title}
            </Link>
            <KanbanListRoleBadge role={summary.card.list_role} />
            {summary.card.priority != null && summary.card.priority !== 5 && (
              <KanbanPriorityBadge priority={summary.card.priority} />
            )}
            {summary.card.due_date != null && (
              <span className="inline-flex items-center gap-1 text-[10px] text-cyan-300 tabular-nums">
                <CalendarClock className="w-3 h-3" />
                {format(new Date(summary.card.due_date), 'MMM d')}
              </span>
            )}
            {summary.card.is_archived === 1 && (
              <span className="chip gap-1">
                <Archive className="w-3 h-3" /> Archived
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-1 text-xs text-glass-400 flex-wrap">
            <Link to={boardHref} className="hover:text-accent-300 transition-colors truncate">
              {summary.board.title}
            </Link>
            <span
              className="chip"
              style={{
                backgroundColor: `${teamColor}20`,
                borderColor: `${teamColor}40`,
                color: teamColor,
              }}
            >
              {summary.board.team}
            </span>
            <span>· {summary.card.list_title}</span>
            <span className="inline-flex items-center gap-1 text-[10px] tabular-nums">
              <Users className="w-3 h-3" />
              {summary.card.assignees_count}
            </span>
            <span className="text-[10px] text-glass-500 tabular-nums">
              Linked {formatDistanceToNow(new Date(link.linked_at), { addSuffix: true })}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <Link to={cardHref} className="btn-icon btn-icon-sm" title="Open card in workspace" aria-label="Open card in workspace">
            <ExternalLink className="w-4 h-4" />
          </Link>
          {canManage && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
              title="Unlink this card"
              aria-label="Unlink this card"
            >
              <Unlink className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-glass-200/10 space-y-3">
          <div>
            <p className="eyebrow mb-1.5">Sync flags</p>
            <div className="flex flex-wrap gap-1.5">
              {SYNC_FLAGS.map((flag) => {
                const on = link[flag.key] === 1;
                const busy = savingFlags[flag.key];
                return (
                  <button
                    key={flag.key}
                    type="button"
                    onClick={() => void handleToggleFlag(flag.key)}
                    disabled={!canManage || busy}
                    title={flag.hint}
                    aria-pressed={on}
                    className={cn(
                      'badge gap-1 cursor-pointer',
                      on
                        ? 'bg-accent-500/20 border-accent-500/40 text-accent-200'
                        : 'bg-glass-100/5 border-glass-200/20 text-glass-400 hover:text-white',
                      (!canManage || busy) && 'opacity-60 cursor-not-allowed',
                    )}
                  >
                    {busy && <Loader className="w-3 h-3 animate-spin" />}
                    {flag.label}
                    <span className="font-mono text-[9px] opacity-70">{on ? 'on' : 'off'}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="eyebrow mb-1.5">Notes</p>
            <div className="flex items-start gap-2">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={!canManage}
                rows={2}
                placeholder="Optional context for this bridge…"
                className="glass-input flex-1 text-xs"
              />
              {canManage && (
                <button
                  type="button"
                  onClick={() => void handleSaveNotes()}
                  disabled={!notesDirty || savingNotes}
                  className="btn-icon btn-icon-sm"
                  title="Save notes"
                  aria-label="Save notes"
                >
                  {savingNotes ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="alert alert-danger text-xs" role="alert">
              <span className="flex-1">{error}</span>
            </div>
          )}
        </div>
      )}

      {confirmDelete && (
        <ConfirmationModal
          isOpen={confirmDelete}
          onClose={() => (deleting ? undefined : setConfirmDelete(false))}
          onConfirm={handleUnlink}
          title="Unlink card?"
          message={`This will remove the bridge between this event and the card "${summary.card.title}". Neither the card nor the event will be deleted.`}
          confirmText="Unlink"
          isLoading={deleting}
        />
      )}
    </div>
  );
};
